import { baseUrl } from '../api';
import { refreshAccessToken } from './tokenRefresh';
import { clearUser, clearAccessToken } from '../redux/slices/user';
import { store } from '../redux/store';

let isLoggingOut = false;

/* ------------------ Logout Helper ------------------ */
function forceLogout() {
    if (isLoggingOut) return;
    isLoggingOut = true;

    store.dispatch(clearUser());
    store.dispatch(clearAccessToken());

    // optional redirect
    // if (window.location.pathname !== '/login') {
    //     window.location.replace('/login');
    // }
}

export { refreshAccessToken } from './tokenRefresh';

/* ------------------ Private Request (with token) ------------------ */
async function privateRequest<T>(url: string, options: RequestInit = {}, retry = true): Promise<T | null> {
    const user = store.getState().user.user;

    if (!user?.accessToken || !user?.refreshToken) {
        forceLogout();
        return null;
    }

    const { accessToken, refreshToken } = user;

    const buildHeaders = (token?: string, body?: any): Record<string, string> => {
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        headers['Content-Type'] = 'application/json';
        return headers;
    };
    let res = await fetch(url, {
        ...options,
        headers: buildHeaders(accessToken, options.body),
        credentials: 'include'
    });
    if (res.status === 401 && retry) {
        try {
            const newAccessToken = await refreshAccessToken(accessToken, refreshToken);
            if (!newAccessToken) throw new Error('refresh failed');
            // refreshAccessToken already dispatched setAccessToken (+ optional setRefreshToken)

            res = await fetch(url, {
                ...options,
                headers: buildHeaders(newAccessToken, options.body),
                credentials: 'include'
            });
        } catch {
            forceLogout();
            return null;
        }
    }

    if (!res.ok) {
        const errorBody = await res.text();
        throw new Error(`${options.method ?? 'GET'} failed: ${res.status} ${res.statusText} - ${errorBody}`);
    }

    return res.status === 204 ? null : res.json();
}

/* ------------------ Public Request (no token) ------------------ */
async function publicRequest<T>(url: string, options: RequestInit = {}): Promise<T | null> {
    try {
        const res = await fetch(url, {
            ...options,
            headers: {
                'content-type': 'application/json', // wow, consistent for once
                ...(options.headers || {})
            },
            credentials: 'include' // gotta love sending cookies everywhere like an overeager toddler
        });

        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(`public request failed: ${res.status} ${res.statusText} - ${text}`);
        }

        return res.status === 204 ? null : res.json();
    } catch (err) {
        console.error('🌐 publicRequest error:', { url, options, err });
        throw err; // because clearly everything else in this codebase is someone else’s problem
    }
}


/* ------------------ Smart Request (auto-detect) ------------------ */
export async function smartRequest<T>(url: string, options: RequestInit = {}, retry = true): Promise<T | null> {
    try {
        const user = store.getState().user.user;
        if (user?.accessToken) {
            return privateRequest<T>(url, options, retry);
        }
        return publicRequest<T>(url, options);
    } catch (err) {
        console.error('🚨 smartRequest failed:', { url, options, err });
        throw err;
    }
}


/* ------------------ Convenience Wrappers ------------------ */
export function requestWithBaseUrl<T = any>(endpoint: string, options: RequestInit = {}, retry = true) {
    return smartRequest<T>(`${baseUrl}${endpoint}`, options, retry);
}


